/**
 * Money, rate and hour arithmetic. Storage is always integers:
 *   amounts  -> minor units (cents)          e.g. 3128.43 USD -> 312843
 *   rates    -> rate_e4 (value × 10 000)     e.g. 26.625      -> 266250
 *   hours    -> minutes                      e.g. 117.5 h     -> 7050
 *   fx       -> rate_e6 (value × 1 000 000)  e.g. 2.6845      -> 2684500
 * Never use floats for stored values or arithmetic on them.
 */

/**
 * Supported currencies, in display order. All keep 2 decimals in storage (UZS tiyin, BYN kopeck);
 * UZS and BYN were added for the Excel migration (decisions-log M7).
 */
export const CURRENCIES = ['USD', 'EUR', 'GEL', 'UZS', 'BYN'] as const;
export type Currency = (typeof CURRENCIES)[number];

export const RATE_SCALE = 10_000;
export const FX_SCALE = 1_000_000;
const MINOR_PER_MAJOR = 100;
/** minutes × rate_e4 / 60 (min→h) / 100 (e4→e2, i.e. into cents). */
const LINE_DIVISOR = 60 * (RATE_SCALE / MINOR_PER_MAJOR);

export class MoneyInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyInputError';
  }
}

const DECIMAL_RE = /^(-)?(\d+)(?:[.,](\d*))?$/;

/**
 * Parses a decimal string (dot or comma separator) into an integer scaled by 10^scale, exactly.
 * Throws when the value has more fractional digits than `scale` allows.
 */
export function parseScaled(input: string | number, scale: number): number {
  const text = String(input).trim().replace(/\s/g, '');
  const match = DECIMAL_RE.exec(text);
  if (!match) throw new MoneyInputError(`Not a number: "${input}"`);
  const [, sign, whole = '0', fractionRaw = ''] = match;
  const fraction = fractionRaw.replace(/0+$/, '');
  if (fraction.length > scale) {
    throw new MoneyInputError(`At most ${scale} decimals allowed: "${input}"`);
  }
  const scaled = Number(whole) * 10 ** scale + Number(fraction.padEnd(scale, '0') || '0');
  if (!Number.isSafeInteger(scaled)) throw new MoneyInputError(`Number too large: "${input}"`);
  return sign ? -scaled : scaled;
}

/** "3128.43" -> 312843. At most 2 decimals. */
export function parseMoney(input: string | number): number {
  return parseScaled(input, 2);
}

/** "26.625" -> 266250. At most 4 decimals. */
export function parseRate(input: string | number): number {
  return parseScaled(input, 4);
}

/** "117.5" -> 7050 minutes. Hours must resolve to whole minutes. */
export function parseHours(input: string | number): number {
  const hundredths = parseScaled(input, 2);
  const minutesTimes100 = hundredths * 60;
  if (minutesTimes100 % 100 !== 0) {
    throw new MoneyInputError(`Hours must be whole minutes: "${input}"`);
  }
  return minutesTimes100 / 100;
}

/** "2.6845" -> 2684500. At most 6 decimals. */
export function parseFxRate(input: string | number): number {
  return parseScaled(input, 6);
}

function floorDiv(numerator: number, divisor: number): number {
  const remainder = ((numerator % divisor) + divisor) % divisor;
  return (numerator - remainder) / divisor;
}

/**
 * Invoice / payout line amount in minor units: the exact floor of hours × rate to the cent
 * (decisions-log M1). Legacy computed Math.floor(h * r * 100) / 100 in doubles, which loses a cent
 * on some inputs; this never does.
 */
export function lineAmountMinor(minutes: number, rateE4: number): number {
  assertInt(minutes, 'minutes');
  assertInt(rateE4, 'rateE4');
  return floorDiv(minutes * rateE4, LINE_DIVISOR);
}

/** floor(amount × part / whole) in integers: a fee prorated by worked hours (V96); `whole` > 0. */
export function prorateMinor(amountMinor: number, part: number, whole: number): number {
  assertInt(amountMinor, 'amountMinor');
  assertInt(part, 'part');
  assertInt(whole, 'whole');
  if (whole <= 0) throw new MoneyInputError('Whole must be positive');
  return floorDiv(amountMinor * part, whole);
}

/** Legacy double-precision rule, kept only for comparison tests and migration reports. */
export function legacyFloatTotal(hours: number, rate: number): number {
  return Math.floor(hours * rate * 100) / 100;
}

/** Converts an amount with an fx rate (quote per base, ×1e6), rounding half away from zero. */
export function convertMinor(amountMinor: number, rateE6: number): number {
  assertInt(amountMinor, 'amountMinor');
  assertInt(rateE6, 'rateE6');
  const product = amountMinor * rateE6;
  const sign = product < 0 ? -1 : 1;
  const abs = Math.abs(product);
  return sign * floorDiv(abs + FX_SCALE / 2, FX_SCALE);
}

/** Inverse of an fx rate (×1e6), e.g. GEL→USD from USD→GEL. */
export function invertRateE6(rateE6: number): number {
  assertInt(rateE6, 'rateE6');
  if (rateE6 <= 0) throw new MoneyInputError('Rate must be positive');
  return Math.round((FX_SCALE * FX_SCALE) / rateE6);
}

/** Cross rate a/b where both are quoted against the same currency, e.g. EUR/GEL ÷ USD/GEL = EUR/USD. */
export function crossRateE6(numeratorE6: number, denominatorE6: number): number {
  if (denominatorE6 <= 0) throw new MoneyInputError('Rate must be positive');
  return Math.round((numeratorE6 * FX_SCALE) / denominatorE6);
}

export function sumMinor(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/** Major-unit number for template variables: 604800 -> 6048, 312843 -> 3128.43 (decisions-log T8). */
export function minorToNumber(minor: number): number {
  return minor / MINOR_PER_MAJOR;
}

export function rateToNumber(rateE4: number): number {
  return rateE4 / RATE_SCALE;
}

export function minutesToHours(minutes: number): number {
  return minutes / 60;
}

export function fxToNumber(rateE6: number): number {
  return rateE6 / FX_SCALE;
}

function assertInt(value: number, name: string): void {
  if (!Number.isSafeInteger(value)) throw new MoneyInputError(`${name} must be an integer, got ${value}`);
}
